"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";

import { fetcher } from "@/lib/api";

/**
 * `GR-BRD01`: one visit from a broadcast's link, counted on arrival.
 *
 * A broadcast's bell row and email link carry a plain utm
 * (`utm_medium=email|bell&utm_campaign=bc-{code}`), the only thing a
 * broadcast measures besides the registrations of the people it reached:
 * no pixel, no open tracking. A link scanner runs no script, so it is never
 * counted here. Once per browser tab per broadcast and channel
 * (`sessionStorage`), posted without the account (`anonymous`), and nothing
 * happens on any other page load.
 */
const CAMPAIGN = /^bc-([a-z2-9]{10})$/;

export function broadcastVisitOf(params: URLSearchParams): { code: string; channel: "email" | "bell" } | null {
  const match = CAMPAIGN.exec(params.get("utm_campaign") ?? "");
  const medium = params.get("utm_medium");
  if (!match || (medium !== "email" && medium !== "bell")) return null;
  return { code: match[1]!, channel: medium };
}

export function BroadcastVisitTracker() {
  const params = useSearchParams();

  useEffect(() => {
    const visit = broadcastVisitOf(params);
    if (!visit) return;
    const key = `progsity:broadcast-visit:${visit.code}:${visit.channel}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, "1");
    } catch {
      // Storage refused (a private window): this load still counts once.
    }
    void fetcher(
      "/v1/broadcasts/visit",
      { method: "POST", body: JSON.stringify(visit), keepalive: true },
      undefined,
      { anonymous: true },
    ).catch(() => undefined);
  }, [params]);

  return null;
}
